import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Input, Select, Label, Modal, Empty } from "../components/UI";
import { Plus, Trash2, Pencil, PlayCircle, Youtube, Upload, BarChart3, Eye, Users, FileText, Newspaper, Rss, ScrollText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const VIDEO_EMPTY = { title: "", description: "", category: "General", resource_type: "video", video_type: "youtube", url: "", audience: "all", client_ids: [] };
const DOC_EMPTY = { title: "", description: "", category: "General", resource_type: "document", doc_kind: "Article", url: "", audience: "all", client_ids: [] };
const CATEGORIES = ["General", "Coping Skills", "Medication Education", "Recovery & Relapse Prevention", "Family Support", "Wellness", "Orientation"];
const DOC_KINDS = [
  { key: "Article", icon: Newspaper },
  { key: "Blog", icon: Rss },
  { key: "Whitepaper", icon: ScrollText },
  { key: "Document", icon: FileText },
];
const kindIcon = (k) => (DOC_KINDS.find((d) => d.key === k) || DOC_KINDS[3]).icon;
const absUrl = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

function VideoThumb({ r }) {
  if (r.video_type === "youtube" && r.youtube_id)
    return <img src={`https://img.youtube.com/vi/${r.youtube_id}/mqdefault.jpg`} alt="" className="h-32 w-full object-cover" />;
  return <div className="flex h-32 w-full items-center justify-center bg-[#3E2723]"><PlayCircle size={36} className="text-[#FFB300]" /></div>;
}

export default function Resources() {
  useEffect(() => { document.title = "Education Resources | COCOA EHR"; }, []);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [tab, setTab] = useState("videos");
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(VIDEO_EMPTY);
  const [editId, setEditId] = useState(null);
  const [docSource, setDocSource] = useState("link");
  const [analytics, setAnalytics] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/education-resources").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const videos = rows.filter((r) => (r.resource_type || "video") !== "document");
  const docs = rows.filter((r) => r.resource_type === "document");
  const isDocTab = tab === "documents";

  const openAdd = () => { setForm(isDocTab ? DOC_EMPTY : VIDEO_EMPTY); setDocSource("link"); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    const base = r.resource_type === "document" ? DOC_EMPTY : VIDEO_EMPTY;
    setForm({ ...base, ...Object.fromEntries(Object.keys(base).map((k) => [k, r[k] ?? base[k]])) });
    setDocSource((r.url || "").startsWith("/api/") ? "upload" : "link");
    setEditId(r.id); setOpen(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/education-resources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, video_type: "upload", url: r.data.url }));
      toast.success("Video uploaded");
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };

  const onDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/education-resources/upload-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, url: r.data.url }));
      toast.success("Document uploaded");
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/education-resources/${editId}`, form);
      else await api.post("/education-resources", form);
      toast.success(editId ? "Resource updated" : "Resource published");
      setOpen(false); setEditId(null); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const remove = async (r) => {
    try { await api.delete(`/education-resources/${r.id}`); toast.success("Resource removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openAnalytics = async (r) => {
    try { const res = await api.get(`/education-resources/${r.id}/analytics`); setAnalytics(res.data); }
    catch (err) { toast.error(apiError(err)); }
  };

  const totalViews = rows.reduce((s, r) => s + (r.view_count || 0), 0);
  const isDocForm = form.resource_type === "document";

  return (
    <div data-testid="resources-page">
      <PageHeader title="Education Resources" subtitle="Publish videos, articles, and documents to the client portal & track engagement"
        action={isAdmin && <Btn data-testid="resource-add-btn" onClick={openAdd}><Plus size={15} /> {isDocTab ? "Add Document / Article" : "Add Video"}</Btn>} />

      <div className="mb-5 grid grid-cols-3 gap-3 max-w-lg">
        {[["Resources", rows.length, PlayCircle], ["Total Views", totalViews, Eye], ["Reached Clients", rows.reduce((s, r) => s + (r.unique_viewers || 0), 0), Users]].map(([l, v, I]) => (
          <Card key={l} className="p-4 flex items-center gap-3">
            <I size={18} className="text-[#7B6157]" />
            <div><div className="font-heading text-xl font-bold text-[#3E2723]">{v}</div><div className="text-[11px] font-semibold text-[#7B6157]">{l}</div></div>
          </Card>
        ))}
      </div>

      <div className="mb-4 flex gap-2">
        {[{ k: "videos", label: `Videos (${videos.length})` }, { k: "documents", label: `Documents & Articles (${docs.length})` }].map(({ k, label }) => (
          <button key={k} data-testid={`edu-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${tab === k ? "bg-[#4E342E] text-white shadow" : "bg-white border border-[#E5DFD3] text-[#6D4C41] hover:bg-[#F5F2EB]"}`}>
            {label}
          </button>
        ))}
      </div>

      {!isDocTab ? (
        videos.length === 0 ? <Empty text="No education videos yet - add your first video" /> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((r) => (
              <Card key={r.id} className="overflow-hidden" data-testid={`resource-card-${r.id}`}>
                <VideoThumb r={r} />
                <div className="p-4">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#7B6157]">
                    {r.video_type === "youtube" ? <Youtube size={12} className="text-[#C62828]" /> : <Upload size={12} />}
                    <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5">{r.category}</span>
                    <span className="ml-auto">{r.audience === "all" ? "All clients" : `${(r.client_ids || []).length} clients`}</span>
                  </div>
                  <div className="mt-1.5 font-heading text-sm font-bold text-[#3E2723]">{r.title}</div>
                  <p className="mt-0.5 text-xs text-[#7B6157] line-clamp-2">{r.description}</p>
                  <div className="mt-3 flex items-center gap-1.5">
                    <button data-testid={`resource-analytics-${r.id}`} onClick={() => openAnalytics(r)}
                      className="flex items-center gap-1 rounded-full bg-[#E3F2FD] px-2.5 py-1 text-[11px] font-bold text-[#1565C0] hover:bg-[#BBDEFB]">
                      <BarChart3 size={11} /> {r.view_count} views · {r.unique_viewers} viewers
                    </button>
                    {isAdmin && (<>
                      <button data-testid={`resource-edit-${r.id}`} onClick={() => openEdit(r)}
                        className="ml-auto p-1.5 rounded-lg text-[#7B6157] hover:bg-[#F5F2EB]"><Pencil size={13} /></button>
                      <button data-testid={`resource-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded-lg text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                    </>)}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      ) : (
        docs.length === 0 ? <Empty text="No documents or articles yet - publish your first one" /> : (
          <div className="grid gap-4 sm:grid-cols-2">
            {docs.map((r) => {
              const KindIcon = kindIcon(r.doc_kind);
              return (
                <Card key={r.id} className="p-4" data-testid={`resource-card-${r.id}`}>
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F5F2EB] text-[#7B6157]"><KindIcon size={18} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#7B6157]">
                        <span className="rounded-full bg-[#EDE7F6] px-2 py-0.5 text-[#5E35B1]">{r.doc_kind || "Document"}</span>
                        <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5">{r.category}</span>
                        <span className="ml-auto">{r.audience === "all" ? "All clients" : `${(r.client_ids || []).length} clients`}</span>
                      </div>
                      <div className="mt-1.5 font-heading text-sm font-bold text-[#3E2723]">{r.title}</div>
                      <p className="mt-0.5 text-xs text-[#7B6157] line-clamp-2">{r.description}</p>
                      <div className="mt-3 flex items-center gap-1.5">
                        <button data-testid={`resource-open-${r.id}`} onClick={() => window.open(absUrl(r.url), "_blank", "noopener")}
                          className="flex items-center gap-1 rounded-full bg-[#F5F2EB] px-2.5 py-1 text-[11px] font-bold text-[#5D4037] hover:bg-[#EDE7DC]">
                          <ExternalLink size={11} /> Open
                        </button>
                        <button data-testid={`resource-analytics-${r.id}`} onClick={() => openAnalytics(r)}
                          className="flex items-center gap-1 rounded-full bg-[#E3F2FD] px-2.5 py-1 text-[11px] font-bold text-[#1565C0] hover:bg-[#BBDEFB]">
                          <BarChart3 size={11} /> {r.view_count} views
                        </button>
                        {isAdmin && (<>
                          <button data-testid={`resource-edit-${r.id}`} onClick={() => openEdit(r)}
                            className="ml-auto p-1.5 rounded-lg text-[#7B6157] hover:bg-[#F5F2EB]"><Pencil size={13} /></button>
                          <button data-testid={`resource-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded-lg text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                        </>)}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )
      )}

      <p className="mt-6 text-[11px] text-[#84675B]" data-testid="edu-staff-disclaimer">
        Everything published here is visible to portal clients with an education-only disclaimer. Content must be educational, must not contain PHI, and does not replace individualized clinical guidance.
      </p>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Resource" : isDocForm ? "Publish a Document or Article" : "Publish a Video"}>
        <form onSubmit={save} className="space-y-4">
          <div><Label>Title</Label><Input data-testid="resource-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
          <div><Label>Description</Label>
            <textarea data-testid="resource-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full rounded-xl border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723] focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" /></div>
          {isDocForm ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</Select></div>
                <div><Label>Type</Label>
                  <Select data-testid="resource-kind-select" value={form.doc_kind} onChange={(e) => setForm({ ...form, doc_kind: e.target.value })}>
                    {DOC_KINDS.map((k) => <option key={k.key}>{k.key}</option>)}
                  </Select></div>
              </div>
              <div><Label>Source</Label>
                <Select data-testid="resource-doc-source-select" value={docSource} onChange={(e) => { setDocSource(e.target.value); setForm({ ...form, url: "" }); }}>
                  <option value="link">External link (article, blog, or whitepaper URL)</option>
                  <option value="upload">Upload file (PDF, DOCX, TXT)</option>
                </Select></div>
              {docSource === "link" ? (
                <div><Label>Link URL</Label><Input data-testid="resource-doc-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://example.org/article" required /></div>
              ) : (
                <div>
                  <Label>File (pdf, doc, docx, txt, or md - max 20 MB)</Label>
                  <input data-testid="resource-doc-file-input" type="file" accept=".pdf,.doc,.docx,.txt,.md" onChange={onDocUpload}
                    className="block w-full text-xs text-[#6D4C41] file:mr-3 file:rounded-full file:border-0 file:bg-[#4E342E] file:px-4 file:py-2 file:text-xs file:font-bold file:text-white" />
                  {uploading && <div className="mt-1 text-xs text-[#7B6157]">Uploading…</div>}
                  {form.url && !uploading && <div className="mt-1 text-xs font-semibold text-[#2E7D32]">✓ File attached</div>}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</Select></div>
                <div><Label>Source</Label>
                  <Select data-testid="resource-type-select" value={form.video_type} onChange={(e) => setForm({ ...form, video_type: e.target.value, url: "" })}>
                    <option value="youtube">YouTube link</option>
                    <option value="upload">Upload video file</option>
                  </Select></div>
              </div>
              {form.video_type === "youtube" ? (
                <div><Label>YouTube URL</Label><Input data-testid="resource-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" required /></div>
              ) : (
                <div>
                  <Label>Video file (mp4/webm, max 60 MB)</Label>
                  <input ref={fileRef} data-testid="resource-file-input" type="file" accept="video/mp4,video/webm,video/quicktime" onChange={onUpload} className="block w-full text-xs text-[#6D4C41] file:mr-3 file:rounded-full file:border-0 file:bg-[#4E342E] file:px-4 file:py-2 file:text-xs file:font-bold file:text-white" />
                  {uploading && <div className="mt-1 text-xs text-[#7B6157]">Uploading…</div>}
                  {form.url && !uploading && <div className="mt-1 text-xs font-semibold text-[#2E7D32]">✓ Video attached</div>}
                </div>
              )}
            </>
          )}
          <div>
            <Label>Audience</Label>
            <Select data-testid="resource-audience-select" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
              <option value="all">All portal clients</option>
              <option value="selected">Specific clients</option>
            </Select>
            {form.audience === "selected" && (
              <select multiple data-testid="resource-clients-select" value={form.client_ids}
                onChange={(e) => setForm({ ...form, client_ids: [...e.target.selectedOptions].map((o) => o.value) })}
                className="mt-2 h-28 w-full rounded-xl border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723]">
                {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="resource-save-btn" type="submit" disabled={busy || uploading}>{busy ? "Saving…" : editId ? "Save Changes" : "Publish"}</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={!!analytics} onClose={() => setAnalytics(null)} title={analytics ? `Analytics: ${analytics.resource.title}` : ""} wide>
        {analytics && (
          <div className="space-y-4" data-testid="resource-analytics-modal">
            <div className="grid grid-cols-2 gap-3 max-w-xs">
              <div className="rounded-xl bg-[#F5F2EB] p-3 text-center"><div className="font-heading text-2xl font-bold text-[#3E2723]">{analytics.total_views}</div><div className="text-[10px] font-semibold text-[#7B6157]">Total views</div></div>
              <div className="rounded-xl bg-[#F5F2EB] p-3 text-center"><div className="font-heading text-2xl font-bold text-[#3E2723]">{analytics.unique_viewers}</div><div className="text-[10px] font-semibold text-[#7B6157]">Unique clients</div></div>
            </div>
            <div>
              <h4 className="font-heading text-xs font-bold text-[#3E2723] mb-2">Who watched</h4>
              {analytics.viewers.length === 0 ? <p className="text-sm text-[#84675B]">No views yet.</p> : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {analytics.viewers.map((v) => (
                    <div key={v.client_id} className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-3.5 py-2 text-sm" data-testid={`viewer-${v.client_id}`}>
                      <Link to={`/clients/${v.client_id}/console`} className="font-semibold text-[#3E2723] hover:underline" data-testid={`viewer-link-${v.client_id}`}>{v.client_name}</Link>
                      <span className="text-xs text-[#7B6157]">{v.count} view{v.count > 1 ? "s" : ""}</span>
                      <span className="ml-auto text-[11px] text-[#84675B]">Last {dayjs(v.last_viewed).format("MMM D, h:mm A")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
